import { useState, useEffect, useMemo } from 'react';
import { useDashboardConfig } from '../context/DashboardContext';
import { loadEngagement, saveEngagement } from '../utils/engagementStore';
import { getGradient } from '../config/gradients';
import EngagementForm from './EngagementForm';
import EngagementMap from './EngagementMap';

const ORG_ID = process.env.REACT_APP_ORG ?? 'fcp-pilot';

export default function EngagementView({ allData, indicators, gradients, selectedYear }) {
  const config = useDashboardConfig();
  const { state, county } = config.org.geography;

  const [submissions, setSubmissions] = useState([]);
  const [selectedIndicatorId, setSelectedIndicatorId] = useState(
    () => indicators?.[0]?.id ?? null
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Default selectedIndicatorId when indicators first loads or changes
  useEffect(() => {
    if (indicators?.length > 0 && !indicators.find(i => i.id === selectedIndicatorId)) {
      setSelectedIndicatorId(indicators[0].id);
    }
  }, [indicators, selectedIndicatorId]);

  // Load submissions on mount
  useEffect(() => {
    loadEngagement(ORG_ID).then(setSubmissions);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allDataSafe = allData || new Map();
  const indicatorConfig = (indicators ?? []).find(i => i.id === selectedIndicatorId) ?? null;
  const result = selectedIndicatorId ? allDataSafe.get(selectedIndicatorId) : null;
  const geography = useMemo(() => {
    if (!result) return [];
    if (selectedYear && result.geographyByYear?.get(selectedYear)) {
      return result.geographyByYear.get(selectedYear);
    }
    return result.geography ?? [];
  }, [result, selectedYear]);

  const currentGradient = useMemo(
    () => getGradient(gradients?.[selectedIndicatorId]),
    [gradients, selectedIndicatorId]
  );

  async function handleSubmit(payload) {
    setSubmitting(true);
    setSubmitError('');
    try {
      const saved = await saveEngagement(ORG_ID, payload);
      setSubmissions(prev => [saved, ...prev]);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setSubmitted(false);
    setSubmitError('');
  }

  return (
    <div className="engagement-view">
      <div className="engagement-form-panel">
        <div style={{ padding: '20px 20px 0' }}>
          <h2 style={{ margin: '0 0 4px', fontFamily: 'var(--font-heading, serif)', fontSize: '1.2rem', color: '#1a3a24' }}>
            Community Voice
          </h2>
          <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: '#555' }}>
            Share a question, observation, or idea about your community.
          </p>
        </div>
        {submitError && (
          <p style={{ margin: '0 20px 12px', fontSize: '0.85rem', color: '#a63535', background: '#fce8e0', padding: '8px 12px', borderRadius: 4 }}>
            {submitError}
          </p>
        )}
        <EngagementForm
          indicators={indicators}
          onSubmit={handleSubmit}
          onReset={handleReset}
          submitting={submitting}
          submitted={submitted}
        />
      </div>

      <div className="engagement-map-panel">
        {(indicators ?? []).length > 1 && (
          <div className="engagement-map-selector">
            <select
              value={selectedIndicatorId ?? ''}
              onChange={e => setSelectedIndicatorId(e.target.value)}
            >
              {(indicators ?? []).map(ind => (
                <option key={ind.id} value={ind.id}>{ind.label}</option>
              ))}
            </select>
          </div>
        )}
        <EngagementMap
          state={state}
          county={county}
          geography={geography}
          indicatorConfig={indicatorConfig}
          gradient={currentGradient}
          submissions={submissions}
          indicators={indicators}
        />
      </div>
    </div>
  );
}
